/**
 * HTML sanitizer using the browser's DOMParser.
 *
 * Strips dangerous elements, inline event handlers, javascript: URIs,
 * data: URIs in navigation attributes, and dangerous SVG/MathML elements.
 *
 * For stronger guarantees, replace this with DOMPurify:
 *   npm install dompurify @types/dompurify
 *   import DOMPurify from "dompurify";
 *   export const sanitizeHtml = (html: string) => DOMPurify.sanitize(html);
 */

const DANGEROUS_TAGS = new Set([
  "script", "iframe", "object", "embed", "form",
  "applet", "base", "link", "meta",
  // SVG/MathML mutation-XSS vectors
  "math", "maction", "semantics", "annotation-xml",
  "svg", "foreignobject",
  // Additional dangerous elements
  "template", "slot", "portal",
]);

const DANGEROUS_URI_SCHEMES = /^\s*(javascript|data|vbscript|blob)\s*:/i;

export function sanitizeHtml(html: string): string {
  if (!html) return "";
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");

    // Remove dangerous elements (iterate until stable — mutation-XSS defense)
    let removed: number;
    do {
      removed = 0;
      for (const tag of DANGEROUS_TAGS) {
        const els = doc.body.querySelectorAll(tag);
        els.forEach((el) => { el.remove(); removed++; });
      }
    } while (removed > 0);

    // Remove all on* event handler attributes and dangerous URIs from every element
    const allEls = doc.body.querySelectorAll("*");
    allEls.forEach((el) => {
      for (const attr of Array.from(el.attributes)) {
        // Strip all event handlers (onclick, onerror, onload, etc.)
        if (attr.name.toLowerCase().startsWith("on")) {
          el.removeAttribute(attr.name);
          continue;
        }
        // Strip dangerous URI schemes in href/src/action/formaction/xlink:href
        const name = attr.name.toLowerCase();
        if (
          (name === "href" || name === "src" || name === "action" ||
           name === "formaction" || name === "xlink:href" || name === "poster" ||
           name === "background" || name === "dynsrc" || name === "lowsrc") &&
          DANGEROUS_URI_SCHEMES.test(attr.value)
        ) {
          el.removeAttribute(attr.name);
        }
      }
      // Strip style attributes containing url() expressions (CSS injection)
      const style = el.getAttribute("style");
      if (style && /url\s*\(/i.test(style)) {
        el.removeAttribute("style");
      }
    });

    return doc.body.innerHTML;
  } catch {
    // If parsing fails, return empty string rather than unsanitized HTML
    return "";
  }
}
